import React from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { useMutation } from "@apollo/react-hooks";
import { Box, Button, Dialog, DialogTitle, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@iconify/icons-mdi/close";
import { InlineIcon } from "@iconify/react";
import { useFormik } from "formik";
import { MyHrDropzoneEditor } from "my-hr/components";
import {
  OcFormEditorAddressSelector,
  OcFormEditorCountrySelector,
  OcFormEditorGenderSelector,
  OcFormEditorMaritalStatusSelector,
  OcFormEditorMessageTextInput,
  OcFormEditorNationalitySelector,
  OcFormEditorStreetTypeSelector,
  OcFormEditorTextInput,
  OcFormEditorValidFromSelector,
  OcFormEditorZipSelector,
} from "oc/components";
import { useOcSnackbar } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";
import { getFormData, getFormValues } from "oc/utils";
import { getAddressType } from "oc/utils/get-address-type";
import { v4 as uuid } from "uuid";
import * as Yup from "yup";
import { useMyHrPostalCodes } from "my-hr/hooks";

type OcFormEditorProps = {
  open: boolean;
  def: any; // Replace 'any' with a more specific type if available
  data: any; // Replace 'any' with a more specific type if available
  onClose: (arg?: any) => void;
  onShowFormEditor: (type: string, modifier?: string) => void;
  modifier?: string;
};

export const OcFormEditor: React.FC<OcFormEditorProps> = ({
  open,
  def,
  data,
  onClose,
  onShowFormEditor,
  modifier,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useOcIsMobile();
  const { isAddressType, isNewAddress } = getAddressType({ def, data });
  const { data: postalCodes } = useMyHrPostalCodes();

  const { setOpenMessage } = useOcSnackbar();

  let { dialogLabel } = def;
  if (def?.type === "invalidateAddress" && modifier) {
    dialogLabel = t(`invalidate_${modifier}`.toUpperCase());
  }

  const { initialValues, validationSchema } = getFormValues({
    def,
    data,
    isAddressType,
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      if (
        modifier &&
        (modifier === "permanentAddress" ||
          modifier === "temporaryAddress" ||
          modifier === "serviceAddress")
      ) {
        const uploadData = {
          locale: i18n.language,
          typeGroup: "invalidateAddress",
          typeGroupId: uuid(),
          values: [
            {
              type: modifier,
              oldValue: "",
              newValue: "",
            },
          ],
          initialMessage: values.initialMessage,
          validFrom: values.validFrom,
        };
        createChangeRequest({ variables: uploadData });
        return;
      }

      const isSendTypeGroup =
        def.type === "name" ||
        def.type === "mothersName" ||
        def.type === "phones" ||
        def.type === "emails" ||
        isAddressType;

      const { data: formValues } = getFormData({ def, data, values });

      const toNameString = (v: any): string => {
        if (v == null) return "";
        if (typeof v === "object" && "name" in v) {
          return String(v.name);
        }
        return String(v);
      };

      const normalizedValues = (formValues || []).map((item: any) => ({
        ...item,
        oldValue: toNameString(item?.oldValue),
        newValue: toNameString(item?.newValue),
      }));

      const uploadData = {
        locale: i18n.language,
        typeGroup: isSendTypeGroup ? def.type : "",
        typeGroupId: isSendTypeGroup ? uuid() : "",
        values: normalizedValues,
        initialMessage: values.initialMessage,
        validFrom: values.validFrom,
      };
      createChangeRequest({ variables: uploadData });
    },
    validationSchema: Yup.object(validationSchema),
  });

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  const [createChangeRequest] = useMutation(def.mutation, {
    onCompleted: () => {
      formik.setSubmitting(false);
      //setSaveButtonDisabled(false);
      setOpenMessage({
        type: "success",
        message: t("OC_USER_UPDATED"),
      });
      onClose(false);
      //reset();
    },
    onError: (error) => {
      console.log({ error });
      formik.setSubmitting(false);
      //setSaveButtonDisabled(false);
      setOpenMessage({
        type: "error",
        message: t("OC_SAVE_FAILED"),
      });
      //reset();
    },
  });

  const { dirty, isSubmitting } = formik;

  const getChangedValues = (
    values: Record<string, any>,
    initialValues: Record<string, any>,
  ) => {
    return Object.entries(values).reduce(
      (acc, [key, value]) => {
        const hasChanged = initialValues[key] !== value;

        if (hasChanged) {
          acc[key] = value;
        }

        return acc;
      },
      {} as Record<string, any>,
    );
  };

  const changedValues = Object.keys(
    getChangedValues(formik.values, initialValues),
  );

  let saveButtonDisabled =
    !dirty ||
    isSubmitting ||
    _.isEqual(changedValues, ["validFrom", "initialMessage"]) ||
    _.isEqual(changedValues, ["validFrom"]);

  if (def?.type === "invalidateAddress") {
    saveButtonDisabled = false;
  }

  let modifiedType = "";
  if (def?.type === "phones" || def?.type === "emails") {
    modifiedType = def && def?.items && def.items[1].name;
  }

  return (
    <Dialog
      fullScreen={isMobile ? true : false}
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose(false);
        }
      }}
      aria-labelledby="oc-form-editor"
      disableEscapeKeyDown
      scroll="body"
      PaperProps={{
        style: {
          padding: "0px 24px 26px 24px",
          width: isMobile ? "auto" : 552,
        },
      }}
    >
      {isMobile && (
        <Box position="absolute" right="16px" top="16px">
          <InlineIcon
            icon={CloseIcon}
            width="24px"
            onClick={() => {
              onClose(false);
            }}
            style={{
              cursor: "pointer",
            }}
          />
        </Box>
      )}
      <DialogTitle id="oc-form-editor-title" style={{ padding: "16px 0px" }}>
        <Box
          style={{
            fontSize: "20px",
            letterSpacing: "0.15px",
            padding: "10px 16px",
            color: theme.palette.darkBase.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-around",
            textAlign: isMobile ? "center" : "unset",
          }}
        >
          <Typography variant="h6">
            {isNewAddress ? t("MY_HR_ADD_NEW_ADDRESS") : t(dialogLabel)}
          </Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <Box display="flex" flexDirection="column" width="100%">
          {def?.items?.map((item: any) => {
            const { type, name, label } = item;
            const { values, errors, handleChange, setFieldValue } = formik;
            const props: Record<string, any> = {
              name,
              label,
              value: values[name],
              onChange:
                type === "datePicker" ||
                  type === "country" ||
                  type === "nationality" ||
                  type === "gender" ||
                  type === "maritalStatus" ||
                  type === "streetType" ||
                  type === "zip" ||
                  type === "city" ||
                  modifier === "phones"
                  ? setFieldValue
                  : handleChange,
              error: errors[name],
            };
            if (item.inputAdornment) {
              props.inputAdornment = item.inputAdornment;
            }
            const isHungary = formik?.values?.country === "HUN" ? true : false;

            switch (type) {
              case "datePicker":
                return (
                  <OcFormEditorValidFromSelector
                    key={name}
                    name={name}
                    value={values[name]}
                    label={label}
                    onChange={setFieldValue}
                    def={def}
                  />
                );
              case "textarea":
                return (
                  <OcFormEditorMessageTextInput
                    key={name}
                    name={name}
                    value={values[name]}
                    label={label}
                    onChange={props.onChange}
                    {...props}
                  />
                );
              case "gender":
                return (
                  <OcFormEditorGenderSelector
                    key={name}
                    name={name}
                    value={values[name]}
                    label={label}
                    onChange={props.onChange}
                  />
                );
              case "maritalStatus":
                return (
                  <OcFormEditorMaritalStatusSelector
                    key={name}
                    name={name}
                    value={values[name]}
                    label={label}
                    onChange={props.onChange}
                  />
                );
              case "nationality":
                return (
                  <OcFormEditorNationalitySelector
                    key={name}
                    name={name}
                    label={label}
                    value={values[name]}
                    onChange={props.onChange}
                  />
                );
              case "country":
                return (
                  <OcFormEditorCountrySelector
                    key={name}
                    name={name}
                    label={label}
                    value={values[name]}
                    onChange={props.onChange}
                  />
                );
              case "addressSelector": {
                const addressType = def?.type;
                return (
                  <OcFormEditorAddressSelector
                    key={name}
                    addressType={addressType}
                    isNewAddress={isNewAddress}
                    data={data}
                    onShowFormEditor={onShowFormEditor}
                    {...props}
                    isMobile={isMobile}
                  />
                );
              }
              case "streetType": {
                const fieldValue = values[name];

                const { value: _origValue, onChange: _origOnChange, ...rest } = props;
                void _origValue;
                void _origOnChange;

                if (isHungary) {
                  return (
                    <OcFormEditorStreetTypeSelector
                      key={name}
                      name={name}
                      label={label}
                      value={fieldValue}
                      onChange={props.onChange}
                      isHungary={isHungary}
                      isMobile={isMobile}
                      {...rest}
                    />
                  );
                } else {
                  const valueForInput =
                    typeof fieldValue === "string"
                      ? fieldValue
                      : fieldValue && typeof (fieldValue as any).name === "string"
                        ? (fieldValue as any).name
                        : "";

                  const handleStreetTypeChange = (
                    e: React.ChangeEvent<HTMLInputElement>,
                  ) => {
                    setFieldValue("streetType", e.target.value);
                    if (props.onChange) {
                      props.onChange(e);
                    }
                  };

                  return (
                    <OcFormEditorTextInput
                      key={name}
                      name={name}
                      label={label}
                      value={valueForInput}
                      onChange={handleStreetTypeChange}
                      {...rest}
                    />
                  );
                }
              }
              case "districtCode": {
                const cityVal = formik.values.city || "";
                const isBudapest = cityVal.startsWith("Budapest");
                if (!isBudapest) return null;
                return (
                  <OcFormEditorTextInput
                    key={name}
                    name={name}
                    label={label}
                    value={values[name]}
                    onChange={formik.handleChange}
                  />
                );
              }
              case "postalCodeSelector":
                if (isHungary) {
                  const handleHungaryPostalChange = (key: string, item: any) => {
                    if (key === "zip") {
                      const zipValue = item;
                      const matched = postalCodes?.find(
                        (pc: any) => pc.code === zipValue || pc.name === zipValue
                      );
                      if (matched) {
                        formik.setFieldValue("city", matched.name);
                        formik.setFieldValue("zip", matched.code);
                      } else {
                        formik.setFieldValue("zip", zipValue);
                      }
                    } else if (key === "city") {
                      const cityValue = item;
                      const matched = postalCodes?.find(
                        (pc: any) => pc.name === cityValue || pc.code === cityValue
                      );
                      if (matched) {
                        formik.setFieldValue("city", matched.name);
                        formik.setFieldValue("zip", matched.code);
                      } else {
                        formik.setFieldValue("city", cityValue);
                      }
                    }
                  };
                  props.onChange = handleHungaryPostalChange;

                  return (
                    <OcFormEditorZipSelector
                      key={name}
                      name={props.name}
                      label={props.label}
                      value={props.value}
                      onChange={props.onChange}
                      isNewAddress={props.isNewAddress}
                    />
                  );
                } else {
                  const handleTextChange = (
                    e: React.ChangeEvent<HTMLInputElement>,
                  ) => {
                    setFieldValue(e.target.name, e.target.value);
                  };
                  props.onChange = handleTextChange;
                  return (
                    <OcFormEditorTextInput
                      key={name}
                      name={props.name}
                      label={props.label}
                      value={props.value}
                      onChange={props.onChange}
                      {...props}
                    />
                  );
                }

              default:
                return (
                  <OcFormEditorTextInput
                    key={name}
                    name={props.name}
                    label={props.label}
                    value={props.value}
                    onChange={props.onChange}
                    {...props}
                  />
                );
            }
          })}
        </Box>
        <Box height="20px" />
        <MyHrDropzoneEditor type={modifiedType ? modifiedType : def?.type} />
        <Box
          style={{
            borderBottom: "1px solid #C6CACC",
            width: isMobile ? "auto" : "551px",
          }}
        />
        <Box width="100%">
          <Box paddingTop="36px">
            <Button
              id="my-hr-user-name-data-editor-save-button"
              fullWidth
              variant="contained"
              color="primary"
              disableElevation
              disabled={saveButtonDisabled}
              type="submit"
            >
              {t("OC_SAVE")}
            </Button>
          </Box>
          {/*
          {isAddressType && !def?.isNewAddress && (
            <Box paddingTop="36px">
              <Button
                fullWidth
                style={{
                  color: theme.palette.primary.main,
                }}
                onClick={() => {
                  handleCancel();
                  onShowFormEditor("invalidateAddress", def?.type);
                }}
              >
                {t("MY_HR_ADDRESS_INVALIDATION")}
              </Button>
            </Box>
          )}
              */}
          <Box paddingTop="36px">
            <Button
              id="my-hr-user-name-data-editor-cancel-button"
              fullWidth
              variant="text"
              color="secondary"
              onClick={handleCancel}
            >
              {t("OC_CANCEL")}
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default OcFormEditor;
